import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as FileIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import { PageHeader } from '../components/ui/PageHeader';
import { DATE_TIME_FORMAT } from '../constants/dateFormats';
import {
  uploadBulkFile,
  openKnowledgeGeneratorDraft,
  getBulkUploadJobs,
  getBulkUploadJob,
  downloadBulkUploadTemplate,
  downloadJobErrors,
  type BulkUploadJob
} from '../services/apiService';
import { validateBulkUploadFile } from '../utils/knowledgeBaseHelpers';

interface UploadError {
  message: string;
  details?: string[];
  hint?: string;
}

const BulkUpload: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDraftTaskId = searchParams.get('draftTaskId');
  const [draftTaskId, setDraftTaskId] = useState<string | null>(requestedDraftTaskId);
  const [openingDraft, setOpeningDraft] = useState(!requestedDraftTaskId);
  const [jobs, setJobs] = useState<BulkUploadJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<UploadError | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'add_missing' | 'replace'>('add_missing');
  const [dragActive, setDragActive] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [expandedJobDetails, setExpandedJobDetails] = useState<BulkUploadJob | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Polling ref to track active polling intervals
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getBulkUploadJobs(pagination.page, pagination.limit);
      setJobs(response.jobs || []);
      setPagination(response.pagination || { page: 1, limit: 10, total: 0, pages: 1 });
    } catch (err: any) {
      setError({ message: err.message || 'Failed to fetch jobs' });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (requestedDraftTaskId) {
      setDraftTaskId(requestedDraftTaskId);
      setOpeningDraft(false);
      return undefined;
    }

    let active = true;
    setOpeningDraft(true);
    openKnowledgeGeneratorDraft()
      .then((draft) => {
        if (!active) return;
        setDraftTaskId(draft._id);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('draftTaskId', draft._id);
        setSearchParams(nextParams, { replace: true });
      })
      .catch((err) => {
        if (active) setError({ message: err instanceof Error ? err.message : 'Could not open a knowledge draft' });
      })
      .finally(() => {
        if (active) setOpeningDraft(false);
      });
    return () => { active = false; };
  }, [requestedDraftTaskId, searchParams, setSearchParams]);

  // Poll for processing jobs
  useEffect(() => {
    const hasProcessingJobs = jobs.some(job => job.status === 'pending' || job.status === 'processing');

    if (hasProcessingJobs && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        fetchJobs();
      }, 3000);
    } else if (!hasProcessingJobs && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobs, fetchJobs]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      validateAndSetFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    const validationResult = validateBulkUploadFile(file);
    if (!validationResult.isValid) {
      setError({ message: validationResult.errorMessage as string });
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!draftTaskId) {
      setError({ message: 'A knowledge draft must be opened before uploading.' });
      return;
    }
    if (!selectedFile) {
      setError({ message: 'Please select a file to upload' });
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const response = await uploadBulkFile(selectedFile, { draftTaskId, importMode });

      if (response.success) {
        setSuccess(`Adding "${selectedFile.name}" to your current draft. You can review everything before publishing.`);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchJobs();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        // Handle detailed error response
        const errorResponse = response as any;
        setError({
          message: errorResponse.error || 'Upload failed',
          details: errorResponse.details,
          hint: errorResponse.hint
        });
      }
    } catch (err: any) {
      // Handle error thrown from API service
      const errorData = err.details || err;
      setError({
        message: errorData.error || err.message || 'Upload failed',
        details: errorData.details,
        hint: errorData.hint
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadBulkUploadTemplate();
    } catch (err: any) {
      setError({ message: err.message || 'Failed to download template' });
    }
  };

  const handleDownloadErrors = async (jobId: string) => {
    try {
      await downloadJobErrors(jobId);
    } catch (err: any) {
      setError({ message: err.message || 'Failed to download errors' });
    }
  };

  const handleExpandJob = async (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      setExpandedJobDetails(null);
    } else {
      try {
        const job = await getBulkUploadJob(jobId);
        setExpandedJobId(jobId);
        setExpandedJobDetails(job);
      } catch (err: any) {
        setError({ message: err.message || 'Failed to fetch job details' });
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-success-tint text-success">
            <CheckCircleIcon style={{ fontSize: '0.875rem' }} />
            Completed
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-warning-tint text-warning">
            <ErrorIcon style={{ fontSize: '0.875rem' }} />
            Partial
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-error-tint text-error">
            <XCircleIcon style={{ fontSize: '0.875rem' }} />
            Failed
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-info-tint text-info">
            <RefreshIcon className="animate-spin" style={{ fontSize: '0.875rem' }} />
            Processing
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-subtle text-ink">
            <PendingIcon style={{ fontSize: '0.875rem' }} />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-subtle text-ink-secondary">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format(DATE_TIME_FORMAT);
  };

  return (
    <MainLayout title="Upload spreadsheet">
      <div className="apexia-kb apexia-kb-page">
        <PageHeader
          title="Upload spreadsheet"
          description="Add objections and FAQs to your current knowledge draft. Nothing goes live until you review and publish it."
          actions={draftTaskId && (
              <Link
                to={`/knowledge-base?setup=review&taskId=${encodeURIComponent(draftTaskId)}`}
                className="px-4 py-2 text-sm font-medium border border-signature bg-surface text-signature hover:bg-signature-tint rounded-lg"
              >
                Review current draft
              </Link>
          )}
        />

        {/* Success/Error Messages */}
      {success && (
          <div role="status" aria-live="polite" className="mb-4 p-4 bg-success-tint border border-success rounded-lg flex items-center">
            <CheckCircleIcon className="text-success mr-2" style={{ fontSize: '1.25rem' }} />
            <span className="text-ink">{success}</span>
          </div>
        )}

      {error && (
          <div role="alert" className="mb-4 p-4 bg-error-tint border border-error rounded-lg">
            <div className="flex items-start">
              <XCircleIcon className="text-error mr-2 mt-0.5 flex-shrink-0" style={{ fontSize: '1.25rem' }} />
              <div className="flex-1">
                <p className="text-ink font-medium">{error.message}</p>
                {error.details && error.details.length > 0 && (
                  <ul className="mt-2 text-sm text-ink-secondary list-disc list-inside space-y-1">
                    {error.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                )}
                {error.hint && (
                  <p className="mt-2 text-sm text-ink-secondary italic">{error.hint}</p>
                )}
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-2 text-error hover:opacity-80 flex-shrink-0"
              >
                <XCircleIcon style={{ fontSize: '1rem' }} />
              </button>
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-surface rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-ink">Choose your file</h2>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-signature hover:text-signature-strong hover:bg-signature-tint rounded-lg transition-colors"
            >
              <DownloadIcon style={{ fontSize: '1.25rem' }} />
              Download Template
            </button>
          </div>

          <fieldset className="mb-4">
            <legend className="text-sm font-medium text-ink mb-2">How should this update the draft?</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className={`border rounded-lg p-3 cursor-pointer ${importMode === 'add_missing' ? 'border-signature bg-signature-tint' : 'border-hairline'}`}>
                <span className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="importMode"
                    value="add_missing"
                    checked={importMode === 'add_missing'}
                    onChange={() => setImportMode('add_missing')}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-ink">Add missing only</span>
                    <span className="block text-xs text-ink-secondary mt-0.5">Recommended. Existing and similar draft entries are left unchanged.</span>
                  </span>
                </span>
              </label>
              <label className={`border rounded-lg p-3 cursor-pointer ${importMode === 'replace' ? 'border-error bg-error-tint' : 'border-hairline'}`}>
                <span className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="importMode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-ink">Replace unpublished draft entries</span>
                    <span className="block text-xs text-ink-secondary mt-0.5">Removes current unpublished objections and FAQs. Published knowledge is not changed.</span>
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          {/* Info Box */}
          <div className="mb-4 p-4 bg-info-tint border border-info rounded-lg">
            <div className="flex items-start gap-2">
              <InfoIcon className="text-info mt-0.5" style={{ fontSize: '1.25rem' }} />
              <div className="text-sm text-ink">
                <p className="font-medium mb-1">Excel File Format:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>objections</strong> sheet: Columns "Objection" and "Response"</li>
                  <li><strong>faqs</strong> sheet: Columns "Question" and "Answer"</li>
                </ul>
                <p className="mt-2">Each row will be processed to generate 5-6 variations and auto-categorized.</p>
              </div>
            </div>
          </div>

          {/* Drag & Drop Zone */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-signature bg-signature-tint'
                : selectedFile
                ? 'border-success bg-success-tint'
                : 'border-ink-border hover:border-ink-muted'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            {selectedFile ? (
              <div className="flex flex-col items-center">
                <FileIcon className="text-success mb-2" style={{ fontSize: '3rem' }} />
                <p className="text-lg font-medium text-ink">{selectedFile.name}</p>
                <p className="text-sm text-ink-muted mt-1">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="mt-2 text-sm text-error hover:opacity-80"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <UploadIcon className="text-ink-muted mb-2" style={{ fontSize: '3rem' }} />
                <p className="text-lg font-medium text-ink">
                  Drag & drop your Excel file here
                </p>
                <p className="text-sm text-ink-muted mt-1">or click to browse</p>
                <p className="text-xs text-ink-muted mt-2">Supported formats: .xlsx, .xls</p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading || openingDraft || !draftTaskId}
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors ${
                !selectedFile || uploading || openingDraft || !draftTaskId
                  ? 'bg-surface-sunken text-ink-disabled cursor-not-allowed'
                  : 'bg-signature text-on-signature hover:bg-signature-strong'
              }`}
            >
              {openingDraft ? (
                <>
                  <RefreshIcon className="animate-spin" style={{ fontSize: '1.25rem' }} />
                  Opening draft...
                </>
              ) : uploading ? (
                <>
                  <RefreshIcon className="animate-spin" style={{ fontSize: '1.25rem' }} />
                  Uploading...
                </>
              ) : (
                <>
                  <UploadIcon style={{ fontSize: '1.25rem' }} />
                  Add to draft
                </>
              )}
            </button>
          </div>
        </div>

        {/* Job History Section */}
        <div className="bg-surface rounded-lg shadow-sm">
          <div className="p-4 border-b border-hairline flex items-center justify-between">
            <h2 className="text-lg font-semibold text-ink">Import history</h2>
            <button
              onClick={fetchJobs}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-ink-secondary hover:text-ink hover:bg-surface-subtle rounded-lg transition-colors"
            >
              <RefreshIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1rem' }} />
              Refresh
            </button>
          </div>

          {loading && jobs.length === 0 ? (
            <div className="p-8 text-center">
              <RefreshIcon className="animate-spin mx-auto text-ink-muted" style={{ fontSize: '2rem' }} />
              <p className="mt-2 text-ink-muted">Loading...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-ink-muted">
              No upload history found. Upload a file to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-subtle border-b border-hairline">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink">Date/Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink">File Name</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-ink">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-ink">Objections</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-ink">FAQs</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-ink">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {jobs.map((job) => (
                    <React.Fragment key={job._id}>
                      <tr className="hover:bg-surface-subtle transition-colors">
                        <td className="px-4 py-3 text-sm text-ink">
                          {formatDate(job.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-ink">
                          <div className="flex items-center gap-2">
                            <FileIcon className="text-ink-muted" style={{ fontSize: '1.25rem' }} />
                            {job.fileName}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(job.status)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm">
                          <span className="text-success font-medium">
                            {job.addedObjections ?? (job.processedObjections - job.failedObjections)}
                          </span>
                          {job.failedObjections > 0 && (
                            <span className="text-error"> / -{job.failedObjections}</span>
                          )}
                          <span className="text-ink-muted"> of {job.totalObjections}</span>
                          {!!job.skippedObjections && <span className="block text-xs text-ink-muted">{job.skippedObjections} similar skipped</span>}
                        </td>
                        <td className="px-4 py-3 text-center text-sm">
                          <span className="text-success font-medium">
                            {job.addedFaqs ?? (job.processedFaqs - job.failedFaqs)}
                          </span>
                          {job.failedFaqs > 0 && (
                            <span className="text-error"> / -{job.failedFaqs}</span>
                          )}
                          <span className="text-ink-muted"> of {job.totalFaqs}</span>
                          {!!job.skippedFaqs && <span className="block text-xs text-ink-muted">{job.skippedFaqs} similar skipped</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleExpandJob(job._id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-signature hover:text-signature-strong hover:bg-signature-tint rounded-lg transition-colors"
                          >
                            {expandedJobId === job._id ? (
                              <>
                                <ExpandLessIcon style={{ fontSize: '1rem' }} />
                                Hide Details
                              </>
                            ) : (
                              <>
                                <ExpandMoreIcon style={{ fontSize: '1rem' }} />
                                View Details
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      {/* Expanded Details Row */}
                      {expandedJobId === job._id && expandedJobDetails && (
                        <tr className="bg-surface-subtle">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="space-y-4">
                              {/* Summary Stats */}
                              <div className="grid grid-cols-4 gap-4">
                                <div className="bg-surface p-3 rounded-lg border border-hairline">
                                  <p className="text-xs text-ink-muted uppercase">Total Items</p>
                                  <p className="text-xl font-semibold text-ink">
                                    {expandedJobDetails.totalObjections + expandedJobDetails.totalFaqs}
                                  </p>
                                </div>
                                <div className="bg-surface p-3 rounded-lg border border-hairline">
                                  <p className="text-xs text-ink-muted uppercase">Processed</p>
                                  <p className="text-xl font-semibold text-success">
                                    {expandedJobDetails.processedObjections + expandedJobDetails.processedFaqs}
                                  </p>
                                </div>
                                <div className="bg-surface p-3 rounded-lg border border-hairline">
                                  <p className="text-xs text-ink-muted uppercase">Added to draft</p>
                                  <p className="text-xl font-semibold text-success">
                                    {(expandedJobDetails.addedObjections ?? 0) + (expandedJobDetails.addedFaqs ?? 0)}
                                  </p>
                                </div>
                                <div className="bg-surface p-3 rounded-lg border border-hairline">
                                  <p className="text-xs text-ink-muted uppercase">Failed</p>
                                  <p className="text-xl font-semibold text-error">
                                    {expandedJobDetails.failedObjections + expandedJobDetails.failedFaqs}
                                  </p>
                                </div>
                              </div>

                              {/* Error List */}
                              {expandedJobDetails.errors && expandedJobDetails.errors.length > 0 && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-ink">
                                      Errors ({expandedJobDetails.errors.length})
                                    </h4>
                                    <button
                                      onClick={() => handleDownloadErrors(job._id)}
                                      className="inline-flex items-center gap-1 px-3 py-1 text-xs text-signature hover:text-signature-strong hover:bg-signature-tint rounded transition-colors"
                                    >
                                      <DownloadIcon style={{ fontSize: '0.875rem' }} />
                                      Download CSV
                                    </button>
                                  </div>
                                  <div className="bg-surface rounded-lg border border-hairline overflow-hidden">
                                    <table className="w-full text-sm">
                                      <thead className="bg-surface-subtle">
                                        <tr>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-ink-secondary">Row</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-ink-secondary">Sheet</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-ink-secondary">Error</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-ink-secondary">Original Data</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-hairline">
                                        {expandedJobDetails.errors.slice(0, 10).map((error, idx) => (
                                          <tr key={idx} className="hover:bg-surface-subtle">
                                            <td className="px-3 py-2 text-ink">{error.row}</td>
                                            <td className="px-3 py-2 text-ink">{error.sheet}</td>
                                            <td className="px-3 py-2 text-error">{error.message}</td>
                                            <td className="px-3 py-2 text-ink-muted text-xs max-w-xs truncate">
                                              {error.originalData?.question || '-'}
                                            </td>
                                          </tr>
                                        ))}
                                        {expandedJobDetails.errors.length > 10 && (
                                          <tr>
                                            <td colSpan={4} className="px-3 py-2 text-center text-ink-muted text-xs">
                                              ... and {expandedJobDetails.errors.length - 10} more errors. Download CSV for full list.
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* No errors message */}
                              {(!expandedJobDetails.errors || expandedJobDetails.errors.length === 0) && (
                                <div className="bg-success-tint border border-success rounded-lg p-4 text-center">
                                  <CheckCircleIcon className="text-success mb-2" style={{ fontSize: '2rem' }} />
                                  <p className="text-ink">All items were processed successfully!</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-4 py-3 border-t border-hairline flex items-center justify-between">
              <div className="text-sm text-ink-secondary">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded text-sm ${
                    pagination.page === 1
                      ? 'bg-surface-subtle text-ink-muted cursor-not-allowed'
                      : 'bg-surface border border-ink-border text-ink-secondary hover:bg-surface-subtle'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className={`px-3 py-1 rounded text-sm ${
                    pagination.page === pagination.pages
                      ? 'bg-surface-subtle text-ink-muted cursor-not-allowed'
                      : 'bg-surface border border-ink-border text-ink-secondary hover:bg-surface-subtle'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default BulkUpload;
